import { lazy, Suspense, useEffect, useState } from "react";
import { AuthForm, type AuthMode } from "./components/auth/AuthForm";
import { LogOutButton } from "./components/auth/LogOutButton";
import { NewPasswordForm } from "./components/auth/NewPasswordForm";
import { Button, Card, CenteredPage } from "./components/ui";
import { authErrorFromUrl, siteService } from "./features";
import { useAuthSession } from "./hooks/useAuthSession";
import { isSiteHostname } from "./lib/hostname";
import { LandingPage } from "./pages/LandingPage";
import { OnboardingPage } from "./pages/OnboardingPage";
import { PrivacyPolicyPage } from "./pages/PrivacyPolicyPage";
import { PublicSitePage } from "./pages/PublicSitePage";
const SitePreviewPage = lazy(() => import("./pages/SitePreviewPage").then(module => ({default:module.SitePreviewPage})));

const LoadingScreen = () => (
  <CenteredPage>
    <span role="status" aria-label="Loading" className="size-9 animate-pulse rounded-xl bg-hazumu-terracotta motion-reduce:animate-none" />
  </CenteredPage>
);

const AuthenticatedApp = () => {
  const [loading, setLoading] = useState(true);
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [error, setError] = useState("");

  const retry = async () => {
    setLoading(true);
    setError("");
    try {
      const sites = await siteService.list();
      setSelectedSiteId(sites[0]?.id ?? null);
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : "Could not load your websites.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    void siteService
      .list()
      .then((sites) => {
        if (active) setSelectedSiteId(sites[0]?.id ?? null);
      })
      .catch((loadError: unknown) => {
        if (active) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : "Could not load your websites.",
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  if (loading) return <LoadingScreen />;

  if (error) {
    return (
      <CenteredPage>
        <Card variant="hero" className="w-full max-w-md text-center">
          <h1 className="font-lora text-2xl font-semibold">
            We couldn’t load your websites
          </h1>
          <p className="mt-3 text-sm leading-6 text-hazumu-muted">{error}</p>
          <div className="mt-6 flex flex-col items-center gap-2">
            <Button variant="primary" size="lg" onClick={retry}>
              Try again
            </Button>
            <LogOutButton />
          </div>
        </Card>
      </CenteredPage>
    );
  }

  return selectedSiteId ? (
    <Suspense fallback={<LoadingScreen />}><SitePreviewPage siteId={selectedSiteId} /></Suspense>
  ) : (
    <OnboardingPage onComplete={setSelectedSiteId} />
  );
};

// Supabase reports a failed email link or cancelled consent in the URL. Read
// it once, tidy the URL, and open the log in form with a note. The form gets
// its own history entry so Back returns to the landing page.
const AUTH_STATE_KEY = "hazumuAuth";

const authModeFromHistory = (): AuthMode | null => {
  const mode = (window.history.state as Record<string, unknown> | null)?.[AUTH_STATE_KEY];
  return mode === "login" || mode === "signup" ? mode : null;
};

const initialUrlError = (() => {
  if (typeof window === "undefined" || isSiteHostname(window.location.hostname)) {
    return null;
  }
  const message = authErrorFromUrl(window.location);
  if (message) {
    window.history.replaceState(null, "", window.location.pathname);
    window.history.pushState({ [AUTH_STATE_KEY]: "login" }, "");
  }
  return message;
})();

const HazumuApp = () => {
  const { session, loading, recovering, endRecovery } = useAuthSession();
  const [authMode, setAuthMode] = useState<AuthMode | null>(authModeFromHistory);
  // Shown once: the note about a failed link goes when the form is left.
  const [urlError, setUrlError] = useState(initialUrlError);

  useEffect(() => {
    const onPopState = () => {
      const mode = authModeFromHistory();
      setAuthMode(mode);
      if (!mode) setUrlError(null);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const openAuth = (mode: AuthMode) => {
    window.history.pushState({ [AUTH_STATE_KEY]: mode }, "");
    setAuthMode(mode);
  };
  const closeAuth = () => {
    setUrlError(null);
    // Back out of the entry we pushed, so Forward reopens the form.
    if (authModeFromHistory()) window.history.back();
    else setAuthMode(null);
  };

  if (loading) return <LoadingScreen />;
  if (!session) {
    return authMode ? (
      <AuthForm
        initialMode={authMode}
        initialError={urlError}
        onBack={closeAuth}
      />
    ) : (
      <LandingPage
        onLogin={() => openAuth("login")}
        onStart={() => openAuth("signup")}
      />
    );
  }
  if (recovering) return <NewPasswordForm onDone={endRecovery} />;
  return <AuthenticatedApp key={session.user.id} />;
};

const App = () => {
  const hostname = window.location.hostname;
  if (isSiteHostname(hostname)) return <PublicSitePage hostname={hostname} />;
  if (window.location.pathname === "/privacy") return <PrivacyPolicyPage />;
  return <HazumuApp />;
};

export default App;
